import styled from 'styled-components'
import ButtonIcon from '@/components/common/ButtonIcon'
import { useEffect, useMemo, useRef } from 'react'
import { NOTIFICATION_DURATION } from '@/common/constants/duration'
import { useActions } from '@/hooks/useActions'
import { useTranslations } from 'next-intl'

const CloseButton = styled(ButtonIcon)`
  position: absolute;
  right: 20px;
  top: 20px;
  background-color: var(--tertiary);
  transition: opacity 0.26s ease-in-out;

  &:hover {
    opacity: 0.8;
  }
`

const NotificationProgressLine = styled.div`
  height: 100%;
  background-color: ${({ color }) => `var(--${color})`};
  animation: ${({ duration }) => `progress ${duration}ms linear`};
  animation-fill-mode: forwards;
`

const NotificationContainer = styled.div`
  padding: 20px 20px 8px;
  box-shadow: 0px 220px 88px rgba(27, 27, 68, 0.02),
    0px 124px 74px rgba(27, 27, 68, 0.08), 0px 55px 55px rgba(27, 27, 68, 0.13),
    0px 14px 30px rgba(27, 27, 68, 0.15), 0px 0px 0px rgba(27, 27, 68, 0.15);
  border-radius: 16px;
  background-color: var(--white);
  transition: transform 0.35s ease-in-out, opacity 0.35s ease-in-out;
  transform: translateX(150%);

  &:hover ${NotificationProgressLine} {
    animation-play-state: paused;
  }

  &.notification-enter {
    transform: translateX(150%);
    opacity: 0;
  }

  &.notification-enter-active,
  &.notification-enter-done {
    opacity: 1;
    transform: translateX(0);
  }

  &.notification-exit {
    opacity: 1;
    transform: translateX(0);
  }

  &.notification-exit-active {
    opacity: 0;
    transform: translateX(150%);
  }

  &.notification-exit-done {
    opacity: 0;
  }
`

const NotificationWrap = styled.div`
  display: flex;
  align-items: center;
  column-gap: 16px;
  margin-bottom: 16px;
  transition: all 0.4s ease-in-out;
`

const NotificationIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 10px;
  background-color: ${({ color }) => color};
`

const NotificationTitle = styled.div`
  margin-bottom: 7px;
  font-size: 16px;
  line-height: 1.2em;
  font-weight: 700;
`
const NotificationText = styled.div`
  font-size: 14px;
  line-height: 1.2em;
  font-weight: 600;
  color: var(--tertiary);
  max-width: 100%;
`

const NotificationProgress = styled.div`
  height: 2px;
  background-color: var(--stroke);
`

const NotificationIcon = styled.div`
  width: 24px;
  height: 24px;
  background: ${({ icon }) =>
    `url(/_next_static/icons/${icon}.svg) no-repeat center/contain`};
`

const typeOptions = {
  error: {
    icon: 'error',
    color: 'red',
    iconWrapColor: 'rgba(247, 65, 25, 0.1)',
  },
  success: {
    icon: 'check-success',
    color: 'success',
    iconWrapColor: 'rgba(163, 208, 33, 0.1)',
  },
  warning: {
    icon: 'warningalert',
    color: 'orange',
    iconWrapColor: 'rgba(217, 198, 33, 0.1)',
  },
}

const notificationTitleMap = {
  success: 'mainSuccess',
  error: 'mainError',
  warning: 'mainWarning',
}

const Notification = ({ type, text, id, className }) => {
  const notificationRef = useRef(null)
  const t = useTranslations()
  const { removeNotification, setNotificationIsVisible } = useActions()
  const typeData = useMemo(() => {
    return typeOptions[type]
  }, [type])
  const localizedText = t(text)
  const correctText = localizedText === text ? text : localizedText

  useEffect(() => {
    setNotificationIsVisible()
    notificationRef.current.addEventListener('animationend', () => {
      removeNotification(id)
    })

    return () => {
      setNotificationIsVisible()
    }
  }, [])

  const onButtonClickHandler = (event) => {
    event.stopPropagation()
    removeNotification(id)
  }

  return (
    <NotificationContainer
      className={className}
      ref={notificationRef}
      type={type}
    >
      <CloseButton
        className="close"
        icon="close"
        color="grey"
        size="16"
        onClick={onButtonClickHandler}
      />
      <NotificationWrap>
        <NotificationIconWrap color={typeData.iconWrapColor}>
          <NotificationIcon icon={typeData.icon} size="24" />
        </NotificationIconWrap>
        <div>
          <NotificationTitle>
            {t(`common.${notificationTitleMap[type]}`)}
          </NotificationTitle>
          <NotificationText>{correctText}</NotificationText>
        </div>
      </NotificationWrap>
      <NotificationProgress>
        <NotificationProgressLine
          duration={NOTIFICATION_DURATION}
          color={typeData.color}
        />
      </NotificationProgress>
    </NotificationContainer>
  )
}

export default Notification
